import { useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Edit } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { Drone, MAX_DRONE_NAME_LENGTH, droneLabel } from '../../types/drone';
import { ApiError } from '../../types/api';
import { droneRenamed } from '../../store/slices/dronesSlice';
import { droneService } from '../../services/droneService';

interface DroneNameEditorProps {
  drone: Drone;
  /** The heading level: the card's title is larger than the side panel's. */
  variant?: 'h5' | 'h6';
}

/**
 * A drone's name, with the control that renames it (#406, G9).
 *
 * Offered on every drone the viewer is shown, because every drone they are
 * shown is one they may command: a pilot sees their own and the unclaimed
 * ones, an admin sees all. Who may rename is the server's decision, and a
 * refusal arrives as one.
 *
 * The rule (1–64 characters, trimmed) is checked here only to say so before
 * sending; the server checks it again, and its message is what is shown when
 * it refuses.
 */
export const DroneNameEditor: React.FC<DroneNameEditorProps> = ({
  drone,
  variant = 'h6',
}) => {
  const dispatch = useDispatch();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = droneLabel(drone);

  const startEditing = () => {
    // An unnamed drone starts empty, not with its ID: the ID is not a name
    // anyone chose, and saving it unchanged would make it one.
    setDraft(drone.name ?? '');
    setError(null);
  };

  const cancel = () => {
    setDraft(null);
    setError(null);
  };

  const save = async () => {
    if (draft === null) return;
    const name = draft.trim();
    if (name === '') {
      setError('A name needs at least one character.');
      return;
    }
    if (name === drone.name) {
      cancel();
      return;
    }
    setSaving(true);
    try {
      await droneService.renameDrone(drone.id, name);
      dispatch(droneRenamed({ id: drone.id, name }));
      cancel();
    } catch (err: unknown) {
      const apiError = err as ApiError;
      setError(apiError?.serverMessage ?? 'The drone could not be renamed.');
    } finally {
      setSaving(false);
    }
  };

  if (draft === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <Typography variant={variant} component="h2" sx={{ overflowWrap: 'anywhere' }}>
          {label}
        </Typography>
        <Tooltip title="Rename">
          <IconButton size="small" onClick={startEditing} aria-label={`Rename ${label}`}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}
    >
      <TextField
        size="small"
        label="Name"
        value={draft}
        autoFocus
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') cancel();
        }}
        error={error !== null}
        helperText={error ?? `Up to ${MAX_DRONE_NAME_LENGTH} characters. Not unique; the ID stays the drone's identity.`}
        inputProps={{ maxLength: MAX_DRONE_NAME_LENGTH }}
        sx={{ flexGrow: 1, minWidth: 180 }}
      />
      <Button type="submit" variant="contained" size="small" disabled={saving} sx={{ mt: 0.5 }}>
        Save
      </Button>
      <Button size="small" onClick={cancel} disabled={saving} sx={{ mt: 0.5 }}>
        Cancel
      </Button>
    </Box>
  );
};
